import { inject, Injectable } from '@angular/core';
import type { TypedDocumentNode } from '@apollo/client/core';
import { catchError, map, type Observable, of } from 'rxjs';

import {
	type DesignChangeCaptureContext,
	DesignChangeRepository,
	type ProjectDesignChange,
	type ProjectDesignChangeDetail,
	type ScopeCreepDesignChangeCards,
} from '@efficiently/client-api-design-changes';
import { GraphqlExecutorService } from '@efficiently/client-core-graphql';
import type { HistoryEventChangeType } from '@efficiently/client-core-graphql-models';

import { DESIGN_CHANGE_CAPTURE_CONTEXT } from '../queries/design-change-capture-context.query';
import { DESIGN_CHANGE_GATED_PROJECT_FINISH_IDS } from '../queries/design-change-gated-project-finish-ids.query';
import { DESIGN_CHANGE_PENDING_PROJECT_FINISH_IDS } from '../queries/design-change-pending-project-finish-ids.query';
import { PROJECT_BID_DESIGN_CHANGE_CARDS } from '../queries/project-bid-design-change-cards.query';
import { PROJECT_BID_DESIGN_CHANGE_ROWS_COUNTER } from '../queries/project-bid-design-change-rows-counter.query';
import { PROJECT_DESIGN_CHANGE } from '../queries/project-design-change.query';
import { PROJECT_DESIGN_CHANGES } from '../queries/project-design-changes.query';
import { PROJECT_DESIGN_CHANGES_PRESENCE } from '../queries/project-design-changes-presence.query';

interface DesignChangesPresenceData {
	projectDesignChanges: Array<{ _id: string }>;
}

@Injectable()
export class DesignChangeGqlRepository extends DesignChangeRepository {
	private readonly gql = inject(GraphqlExecutorService);

	public override getCaptureContext(projectFinishIds: string[]): Observable<DesignChangeCaptureContext> {
		return this.gql.query(DESIGN_CHANGE_CAPTURE_CONTEXT, {
			variables: { projectFinishIds },
			select: data => data.designChangeCaptureContext,
			// eslint-disable-next-line @typescript-eslint/naming-convention
			skipGlobalErrorHandler: true,
		});
	}

	public override getPendingProjectFinishIds(): Observable<string[]> {
		return this.gql.query(DESIGN_CHANGE_PENDING_PROJECT_FINISH_IDS, {
			select: data => data.designChangePendingProjectFinishIds,
		});
	}

	public override getGatedProjectFinishIds(): Observable<string[]> {
		return this.gql.query(DESIGN_CHANGE_GATED_PROJECT_FINISH_IDS, {
			select: data => data.designChangeGatedProjectFinishIds,
		});
	}

	public override getDesignChanges(): Observable<ProjectDesignChange[]> {
		return this.gql.query(PROJECT_DESIGN_CHANGES, {
			select: data => data.projectDesignChanges,
		});
	}

	public override hasDesignChanges(): Observable<boolean> {
		return this.gql
			.query<DesignChangesPresenceData, Record<string, never>, DesignChangesPresenceData['projectDesignChanges']>(
				PROJECT_DESIGN_CHANGES_PRESENCE as TypedDocumentNode<DesignChangesPresenceData, Record<string, never>>,
				{
					select: data => data.projectDesignChanges,
					// eslint-disable-next-line @typescript-eslint/naming-convention
					skipGlobalErrorHandler: true,
				},
			)
			.pipe(
				map(designChanges => designChanges.length > 0),
				catchError(() => of(false)),
			);
	}

	public override getDesignChange(id: string): Observable<ProjectDesignChangeDetail | null> {
		return this.gql.query(PROJECT_DESIGN_CHANGE, {
			variables: { _id: id },
			select: data => data.projectDesignChange,
		});
	}

	public override getScopeCreepCards(
		projectBidId: string,
		eventType: HistoryEventChangeType | null,
	): Observable<ScopeCreepDesignChangeCards> {
		return this.gql.query(PROJECT_BID_DESIGN_CHANGE_CARDS, {
			variables: { projectBidId, eventType },
			select: data => data.projectBidDesignChangeCards,
			// eslint-disable-next-line @typescript-eslint/naming-convention
			skipGlobalErrorHandler: true,
		});
	}

	public override getScopeCreepRowsCounter(projectBidId: string): Observable<number> {
		return this.gql.query(PROJECT_BID_DESIGN_CHANGE_ROWS_COUNTER, {
			variables: { projectBidId },
			select: data => data.projectBidDesignChangeRowsCounter,
			// eslint-disable-next-line @typescript-eslint/naming-convention
			skipGlobalErrorHandler: true,
		});
	}
}
